import { css, cx } from '@linaria/core'
import { type FC, type HTMLProps, useState } from 'react'
import { Icon } from '#components/Icon'
import EditIcon from '#icons/edit-24.svg'
import ReturnIcon from '#icons/return.svg'
import { fragments, style } from '#styles/index'

const titleInputClass = css`
  display: flex;
  position: relative;
  background: ${style.themeColors.backgroundDefault};

  
  > .input {
    width: 100%;
    
    &:not(.slim) {
      ${fragments.textStyle.heading.xl.regular};
    }
    
    &.slim {
      font-size: inherit;
    }
    
    background-color: transparent;
    border-bottom: 2px solid transparent;
    z-index: 1;
    flex: 1;

    border-left: none;
    border-right: none;
    border-top: none;
    padding: 0 22px 0 2px;

    color: ${style.themeColors.textDefault};
    transition: 0.25s ease border-bottom-color;
    
    &::placeholder {
      color: ${style.themeColors.textDisabled};
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &:disabled {
      color: ${style.themeColors.textDisabled};
      &:hover {
        border-bottom-color: transparent;
      }
    }

    &.with-error {
      color: ${style.themeColors.textDanger};
      border-bottom-color: ${style.themeColors.textImportant};
    }

    &:focus {
      border-bottom-color: ${style.themeColors.lineFocus};
      outline: none;
    }
    
    &:not(:focus) {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  > .edit-icon {
    pointer-events: none;
    position: absolute;
    color: transparent;
    z-index: 0;
    right: 2px;
    transform: translate(0, 50%);
    transition: 0.25s ease color;

    &.disabled {
      color: transparent ;
      &:hover {
        color: transparent;
      }
    }
  }

  // apply coloring on the parent hover to synchronize styling of input and icon
  &:hover {
    > .input {
      border-bottom-color: ${style.themeColors.textImportant};
    }
    > .edit-icon {
      color: ${style.themeColors.textImportant};
      &.disabled {
        color: transparent;
      }
    }
  }

  &:focus-within > .edit-icon {
    color: ${style.themeColors.textDefault}
  }
`

export type TitleInputProps = Omit<HTMLProps<HTMLInputElement>, 'type' | 'label'> & {
  error?: string | boolean
  slim?: boolean
  type: 'title'
  ref?: React.Ref<HTMLInputElement>
}

export const TitleInput: FC<TitleInputProps> = function TitleInput({ ref, error, slim, className, onFocus, onBlur, disabled, ...props }) {
  const [hasFocus, setHasFocus] = useState(false)
  return (
    <div className={cx(titleInputClass, className)}>
      <input
        ref={ref}
        className={cx('input', !!error && 'with-error', slim && 'slim')}
        onFocus={(evt) => {
          setHasFocus(true)
          onFocus?.(evt)
        }}
        onBlur={(evt) => {
          setHasFocus(false)
          onBlur?.(evt)
        }}
        disabled={disabled}
        {...props}
      />
      <Icon className={cx('edit-icon', disabled && 'disabled')} iconUrl={hasFocus ? ReturnIcon : EditIcon} size={slim ? 'xxs' : 's'} />
    </div>
  )
}
